import { Dispatch, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import { CSSObject } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import { colors } from "@dotnet/telematics-design-system";

import { useStyles as sharedStyles } from "../../shared";

interface TabsCustomProps {
  setValue: Dispatch<SetStateAction<number>>;
  value: number;
  tabsArray: string[];
  customStyle?: CSSObject;
  hasAccess?: boolean;
  tabCustomStyle?: CSSObject;
  translationKey?: string;
  customTabsHolderStyle?: CSSObject;
  tabIndicatorBottom?: string;
  resetDefaultView?: () => void;
}

const TabsCustom = ({
  setValue,
  value,
  tabsArray,
  customStyle,
  hasAccess,
  tabCustomStyle,
  translationKey = "Details##",
  customTabsHolderStyle,
  tabIndicatorBottom,
  resetDefaultView
}: TabsCustomProps) => {
  const { t } = useTranslation();

  const { customTab, tabsHolder } = sharedStyles();

  const isTabDisabled = (tabIndex: number) => tabIndex === 1 && !hasAccess;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    event.preventDefault();
    setValue(newValue);
    resetDefaultView?.(); // Call resetDefaultView if provided
  };

  return (
    <Box
      data-testid="tabs-box"
      sx={[
        {
          borderBottom: 1,
          borderColor: "divider"
        },
        customStyle as CSSObject
      ]}
    >
      <Tabs
        slotProps={{
          indicator: {
            style: { background: colors.blue700, bottom: tabIndicatorBottom }
          }
        }} // override selected item box shadow
        value={value}
        onChange={handleChange}
        aria-label="details tabs"
        data-testid="tabs-holder"
        sx={[tabsHolder, customTabsHolderStyle] as CSSObject[]}
      >
        {tabsArray.map((tab: string, index) => (
          <Tab
            key={tab}
            label={t(`${translationKey}${tab}`)}
            sx={[customTab, tabCustomStyle] as CSSObject[]}
            className={isTabDisabled(index) ? undefined : "enabled"}
            disabled={isTabDisabled(index)}
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default TabsCustom;
